import React from "react";
import { useLocation } from "react-router-dom";
const pages: Record<
  string,
  { title: string; intro: string; sections: [string, string][] }
> = {
  "/trust": {
    title: "Trust in the spiritual-practice record",
    intro:
      "Manifestation, law of attraction, law of assumption, tarot, psychic work, mediumship, astrology, energy practice, and spiritual teaching deserve category-aware research and category-neutral rules.",
    sections: [
      [
        "The structural promise",
        "A review here cannot be deleted by its subject. The subject can always reply. Every claim carries its evidence class on its face.",
      ],
      [
        "What neutrality means",
        "Observer Theory can document what a person teaches, publishes, and offers without deciding whether a manifestation method works, a reading was accurate, a prediction came true, or a spiritual belief is valid.",
      ],
      [
        "Evidence classes",
        "Observed facts are sourced and dated. Self-described material is labelled. Human assertions require moderation. Unknown facts remain unknown.",
      ],
      [
        "Independence",
        "Payment, sponsorship, claim status, and promotion cannot affect ratings, moderation, verification, disputes, or organic rank.",
      ],
    ],
  },
  "/trust/ranking": {
    title: "How search is ordered",
    intro:
      "Name relevance comes first, and every result explains why it appeared.",
    sections: [
      [
        "Organic inputs",
        "Name and alias relevance lead search. The labeled Most Established / Most Active browse sort uses record completeness, recency, activity, and sourced channel counts. Recently added and A–Z are also available.",
      ],
      [
        "Never inputs",
        "Payment, sponsorship, Pro status, claim status, review sentiment, transparency observations, and composite quality scores are never inputs. Popularity never enters review dimensions or moderation.",
      ],
      [
        "Safety actions",
        "A policy-based safety action may reduce exposure only with an audit record and appeal.",
      ],
    ],
  },
  "/trust/reviews": {
    title: "Reviews of teachings, readings, coaching, and spiritual services",
    intro:
      "Evidence outranks volume, and moderation is policy-based rather than sentiment-based.",
    sections: [
      [
        "Three distinct experiences",
        "A paid manifestation course, tarot reading, psychic or mediumship session, astrology consultation, energy practice, or coaching relationship is different from an opinion about free teachings. Observer Theory keeps those evidence classes separate.",
      ],
      [
        "Verified purchase",
        "Evidence is checked by Observer Theory, stored separately, never published, and never exposed to the practitioner.",
      ],
      [
        "Removal grounds",
        "Fabrication, a false conflict declaration, private information, threats or harassment, off-topic content, reviewer withdrawal, or legal requirement. Criticism and commercial inconvenience are not grounds.",
      ],
      [
        "Review dimensions",
        "Delivered as described; communication and responsiveness; boundaries and professionalism; value for money; and audience / community conduct. Audience conduct rates only behaviour a reviewer personally observed and does not attribute an audience's behaviour to the practitioner. Dimensions are never averaged into an overall rating, remain hidden below five actual ratings, never affect organic rank, and never judge spiritual truth, psychic accuracy, or manifestation outcomes.",
      ],
    ],
  },
  "/trust/disputes": {
    title: "Disputes, corrections, and appeals",
    intro:
      "Replies, disputes, and factual corrections are free and never require claiming.",
    sections: [
      [
        "Review disputes",
        "Target resolution is 48 hours. A neutral Under review marker is shown while a dispute is open.",
      ],
      [
        "Appeal",
        "Both sides receive a reason and may appeal once. Corrections supersede the prior fact; history is retained.",
      ],
    ],
  },
  "/trust/seeding": {
    title: "How the first profiles are selected",
    intro:
      "Observer Theory builds its opening factual record across manifestation, law of attraction, law of assumption, tarot, psychic work, mediumship, astrology, energy practice, coaching, and spiritual teaching with an auditable automated gate.",
    sections: [
      [
        "Discovery",
        "Supply Scout continuously searches approved YouTube query families, deduplicates channel IDs, and enriches the largest and most active candidates first. Subscriber, view, video, and recency counts are displayed as dated facts and used only in the labeled established/active sort.",
      ],
      [
        "Automated factual publication",
        "AutoPublicationGate publishes public commercial practitioners when commercial evidence, two identity signals including one bidirectional signal, minimum sourced fields, category confidence handling, adult/private-person filters, and safety holds pass. Its version and reasons are audited. Low-confidence categories publish Tier B and enter sampling audit; there is no human per-listing approval.",
      ],
      [
        "Breadth before review depth",
        "The opening set targets 100–200 profiles across practices before outreach. Friendly practitioners may help broaden coverage, but they receive no better moderation, verification, search placement, or rating treatment. No early participant may build review depth through preferential handling.",
      ],
      [
        "Review safeguards",
        "Observer Theory never imports or fabricates reviews. Early review invitations use standardised copy. Dimension scores remain hidden below five verified reviews, and invitation volume is throttled during the first 90 days when a practitioner's verified-review count would exceed three times the current site median. Reviews themselves are never throttled.",
      ],
      [
        "Visible accountability",
        "Claimed profiles explain that the practitioner invites reviews through Observer Theory. Early participants are told that fabricated reviews are removed and that removal counts are public.",
      ],
    ],
  },
  "/trust/methodology": {
    title: "Factual publication methodology",
    intro: "Publish facts; gate judgments. The Record and the Verdict are permanently separated.",
    sections: [
      ["Tier A — Established", "Commercial evidence, activity within twelve months, and at least ten videos, 5,000 subscribers, or 250,000 views. Tier A records are indexable and appear in browse, category hubs, comparisons, and sitemaps."],
      ["Tier B — Emerging", "Commercial evidence below established thresholds, low activity, or a category awaiting sampling audit. The exact-name record is public but noindex and excluded from default browse. A verified claim, first verified review, or threshold crossing promotes it automatically."],
      ["Excluded", "No commercial evidence, unmatched identity, a minor, a private individual, a dead hobby channel without an offer, or an open safety hold. Excluded candidates do not publish."],
      ["Assertions", "Every factual assertion has a type, source, observation date, confidence, and supersession history. Unknown remains unknown. Automated category sampling has an instant correction and hold path."],
    ],
  },
  "/trust/independence": {
    title: "Independence boundaries",
    intro: "The trust surface is structurally independent from money.",
    sections: [
      ["Organic means organic", "Paid status, Pro, sponsorship, PPC, leads, billing, and claim state are absent from organic ranking inputs. Future sponsored units are labeled, visually separate slots."],
      ["Verdicts cannot be bought", "Payment never affects review dimensions, verification, evidence decisions, moderation, disputes, appeals, badges, or the free right of reply."],
      ["Trust-neutral Pro", "Future Pro buys tools, aggregate profile analytics, and an enquiry inbox only. Referral rewards bank Pro credit and post only on a verified claim; credit cannot purchase a trust outcome."],
      ["Launch state", "Reviews, Pro, sponsored placement, and lead monetization remain disabled until their explicit gates are complete. Factual records publish independently."],
    ],
  },
};
export default function ObserverTrustPage() {
  const { pathname } = useLocation();
  const p = pages[pathname] || pages["/trust"];
  return (
    <div className="ot-page">
      <header className="ot-page-head">
        <span className="section-kicker">Trust centre</span>
        <h1>{p.title}</h1>
        <p>{p.intro}</p>
      </header>
      <div className="ot-trust-grid">
        {p.sections.map(([h, b]) => (
          <section key={h}>
            <h2>{h}</h2>
            <p>{b}</p>
          </section>
        ))}
      </div>
    </div>
  );
}
